/**
 * Keplerian two-body mechanics: element conversions, anomaly solvers and
 * analytic propagation.
 *
 * Everything here is exact two-body — no perturbations. It is used for three
 * things: converting between the state-vector representation the integrator wants
 * and the element representation users think in; drawing orbit geometry, where an
 * analytic conic is both faster and smoother than integrating; and providing the
 * reference solution the numerical integrators are tested against.
 */

import { TAU } from './constants.js';
import { clampUnit, wrapTwoPi } from './units.js';
import {
  cross,
  dot,
  norm,
  normalize,
  scale,
  type ReadonlyVec3,
  type Vec3,
} from './vec3.js';

/** Below this eccentricity an orbit is treated as circular. */
export const CIRCULAR_TOLERANCE = 1e-8;
/** Below this inclination (rad) an orbit is treated as equatorial. */
export const EQUATORIAL_TOLERANCE = 1e-8;

export interface OrbitalElements {
  /** Semi-major axis, m. Negative for hyperbolic orbits, Infinity for parabolic. */
  semiMajorAxis: number;
  /** Eccentricity, dimensionless. */
  eccentricity: number;
  /** Inclination, rad, in [0, pi]. */
  inclination: number;
  /** Right ascension of the ascending node, rad. */
  raan: number;
  /** Argument of periapsis, rad. */
  argumentOfPeriapsis: number;
  /** True anomaly, rad. */
  trueAnomaly: number;
  /**
   * True when the orbit is circular and the argument of periapsis is therefore
   * undefined. `argumentOfPeriapsis` is set to 0 and `trueAnomaly` carries the
   * argument of latitude instead.
   */
  circular?: boolean;
  /**
   * True when the orbit is equatorial and the node is therefore undefined.
   * `raan` is set to 0 and the in-plane angles are measured from the x axis.
   */
  equatorial?: boolean;
}

export interface StateVector {
  position: Vec3;
  velocity: Vec3;
}

/** Semi-latus rectum p = a(1 - e^2), m. Well behaved for every conic type. */
export function semiLatusRectum(elements: OrbitalElements): number {
  const { semiMajorAxis: a, eccentricity: e } = elements;
  if (!Number.isFinite(a)) return 0;
  return a * (1 - e * e);
}

/** Mean motion, rad/s. Only defined for closed orbits. */
export const meanMotion = (semiMajorAxis: number, mu: number): number =>
  Math.sqrt(mu / Math.abs(semiMajorAxis) ** 3);

/** Orbital period, s. Infinite for parabolic and hyperbolic orbits. */
export function orbitalPeriod(semiMajorAxis: number, mu: number): number {
  if (semiMajorAxis <= 0 || !Number.isFinite(semiMajorAxis)) return Number.POSITIVE_INFINITY;
  return TAU * Math.sqrt(semiMajorAxis ** 3 / mu);
}

/** Specific orbital energy, J/kg. Negative when bound. */
export const specificEnergy = (speed: number, radius: number, mu: number): number =>
  (speed * speed) / 2 - mu / radius;

/** Vis-viva: orbital speed at a radius on an orbit of given semi-major axis. */
export const visViva = (radius: number, semiMajorAxis: number, mu: number): number =>
  Math.sqrt(Math.max(0, mu * (2 / radius - 1 / semiMajorAxis)));

/** Circular orbit speed at a radius, m/s. */
export const circularSpeed = (radius: number, mu: number): number => Math.sqrt(mu / radius);

/** Escape speed at a radius, m/s. */
export const escapeSpeed = (radius: number, mu: number): number => Math.sqrt((2 * mu) / radius);

// --- Anomaly conversions -----------------------------------------------------

/** Eccentric anomaly from true anomaly (elliptic). */
export function eccentricFromTrue(trueAnomaly: number, e: number): number {
  const cosNu = Math.cos(trueAnomaly);
  const sinNu = Math.sin(trueAnomaly);
  // atan2 form rather than the textbook tan(E/2) relation: it stays single-valued
  // across all four quadrants without any branch fixing.
  return Math.atan2(Math.sqrt(1 - e * e) * sinNu, cosNu + e);
}

/** True anomaly from eccentric anomaly (elliptic). */
export function trueFromEccentric(eccentricAnomaly: number, e: number): number {
  const cosE = Math.cos(eccentricAnomaly);
  const sinE = Math.sin(eccentricAnomaly);
  return Math.atan2(Math.sqrt(1 - e * e) * sinE, cosE - e);
}

/** Mean anomaly from eccentric anomaly (Kepler's equation, forward direction). */
export const meanFromEccentric = (eccentricAnomaly: number, e: number): number =>
  eccentricAnomaly - e * Math.sin(eccentricAnomaly);

/** Hyperbolic anomaly from true anomaly. */
export function hyperbolicFromTrue(trueAnomaly: number, e: number): number {
  const cosNu = Math.cos(trueAnomaly);
  const sinNu = Math.sin(trueAnomaly);
  return Math.asinh((sinNu * Math.sqrt(e * e - 1)) / (1 + e * cosNu));
}

/** True anomaly from hyperbolic anomaly. */
export function trueFromHyperbolic(H: number, e: number): number {
  const coshH = Math.cosh(H);
  const sinhH = Math.sinh(H);
  return Math.atan2(-Math.sqrt(e * e - 1) * sinhH, 1 - e * coshH) + Math.PI;
}

/** Hyperbolic mean anomaly from hyperbolic anomaly. */
export const meanFromHyperbolic = (H: number, e: number): number => e * Math.sinh(H) - H;

/**
 * Solve Kepler's equation `M = E - e sin E` for the eccentric anomaly.
 *
 * Newton–Raphson from a starting guess that is accurate enough to converge in
 * three or four iterations even at e = 0.99. Naive `E0 = M` stalls badly for
 * highly eccentric orbits near periapsis, which is exactly where a Molniya or a
 * transfer injection spends its interesting moments.
 */
export function solveKeplerElliptic(meanAnomaly: number, e: number, tolerance = 1e-12): number {
  const M = wrapTwoPi(meanAnomaly);
  if (e < CIRCULAR_TOLERANCE) return M;

  // Starting guess: for moderate e, M works; near periapsis of a very eccentric
  // orbit use the cube-root expansion of the near-parabolic limit.
  let E: number;
  if (e < 0.8) {
    E = M;
  } else {
    const sinM = Math.sin(M);
    E = M + (sinM >= 0 ? 0.85 * e : -0.85 * e);
  }

  for (let i = 0; i < 60; i++) {
    const f = E - e * Math.sin(E) - M;
    const fPrime = 1 - e * Math.cos(E);
    // fPrime approaches 1 - e near periapsis; guard against a division blow-up
    // for e extremely close to 1.
    const step = f / (Math.abs(fPrime) < 1e-12 ? (fPrime < 0 ? -1e-12 : 1e-12) : fPrime);
    E -= step;
    if (Math.abs(step) < tolerance) break;
  }
  return E;
}

/**
 * Solve the hyperbolic Kepler equation `M = e sinh H - H` for H.
 * Newton–Raphson with a logarithmic starting guess, which is essential because
 * sinh grows fast enough that a linear guess diverges.
 */
export function solveKeplerHyperbolic(meanAnomaly: number, e: number, tolerance = 1e-12): number {
  const M = meanAnomaly;
  let H = Math.abs(M) < 6 ? M / (e - 1) : Math.sign(M) * Math.log((2 * Math.abs(M)) / e + 1.8);

  for (let i = 0; i < 100; i++) {
    const f = e * Math.sinh(H) - H - M;
    const fPrime = e * Math.cosh(H) - 1;
    const step = f / fPrime;
    H -= step;
    if (Math.abs(step) < tolerance) break;
  }
  return H;
}

/** True anomaly from mean anomaly, for any conic type. */
export function trueFromMean(meanAnomaly: number, e: number): number {
  if (e < 1) return trueFromEccentric(solveKeplerElliptic(meanAnomaly, e), e);
  if (e > 1) return trueFromHyperbolic(solveKeplerHyperbolic(meanAnomaly, e), e);
  // Parabolic: Barker's equation has a closed-form solution.
  const B = 1.5 * meanAnomaly;
  const A = Math.cbrt(B + Math.sqrt(B * B + 1));
  return 2 * Math.atan(A - 1 / A);
}

/** Mean anomaly from true anomaly, for any conic type. */
export function meanFromTrue(trueAnomaly: number, e: number): number {
  if (e < 1) return meanFromEccentric(eccentricFromTrue(trueAnomaly, e), e);
  if (e > 1) return meanFromHyperbolic(hyperbolicFromTrue(trueAnomaly, e), e);
  const D = Math.tan(trueAnomaly / 2);
  return (D + (D * D * D) / 3) / 1.5;
}

// --- State <-> elements ------------------------------------------------------

/**
 * Convert a state vector to classical orbital elements.
 *
 * Handles the degenerate cases explicitly. A circular orbit has no periapsis, so
 * there is no argument of periapsis to report; an equatorial orbit has no
 * ascending node, so there is no RAAN. Returning NaN or silently zero for these
 * is how orbit editors end up displaying garbage for the GEO case, which is one
 * of the most common orbits a user will ask for.
 */
export function stateToElements(
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  mu: number,
): OrbitalElements {
  const rMag = norm(position);
  const vMag = norm(velocity);
  if (rMag === 0) throw new Error('stateToElements: position vector has zero length');

  const h = cross(position, velocity);
  const hMag = norm(h);

  // Node vector: intersection of the orbital and equatorial planes.
  const n: Vec3 = [-h[1], h[0], 0];
  const nMag = norm(n);

  // Eccentricity vector points at periapsis.
  const rDotV = dot(position, velocity);
  const eVec: Vec3 = [
    ((vMag * vMag - mu / rMag) * position[0] - rDotV * velocity[0]) / mu,
    ((vMag * vMag - mu / rMag) * position[1] - rDotV * velocity[1]) / mu,
    ((vMag * vMag - mu / rMag) * position[2] - rDotV * velocity[2]) / mu,
  ];
  const e = norm(eVec);

  const energy = specificEnergy(vMag, rMag, mu);
  // A parabolic orbit has zero energy and infinite semi-major axis.
  const a = Math.abs(energy) < 1e-12 ? Number.POSITIVE_INFINITY : -mu / (2 * energy);

  const inclination = hMag === 0 ? 0 : Math.acos(clampUnit(h[2] / hMag));
  const isEquatorial = nMag < EQUATORIAL_TOLERANCE * rMag || inclination < EQUATORIAL_TOLERANCE;
  const isCircular = e < CIRCULAR_TOLERANCE;

  // RAAN and the argument of periapsis stay at zero in the degenerate cases where
  // they are genuinely undefined; the true anomaly is always assigned below.
  let raan = 0;
  let argumentOfPeriapsis = 0;
  let trueAnomaly: number;

  if (!isEquatorial) {
    raan = wrapTwoPi(Math.atan2(n[1], n[0]));
  }

  if (isCircular && isEquatorial) {
    // True longitude, measured from the x axis in the equatorial plane.
    trueAnomaly = wrapTwoPi(
      Math.atan2(position[1], position[0]) * (h[2] < 0 ? -1 : 1),
    );
  } else if (isCircular) {
    // Argument of latitude, measured from the ascending node.
    const u = Math.acos(clampUnit(dot(n, position) / (nMag * rMag)));
    trueAnomaly = wrapTwoPi(position[2] < 0 ? -u : u);
  } else if (isEquatorial) {
    // Longitude of periapsis, measured from the x axis.
    argumentOfPeriapsis = wrapTwoPi(Math.atan2(eVec[1], eVec[0]) * (h[2] < 0 ? -1 : 1));
    const nu = Math.acos(clampUnit(dot(eVec, position) / (e * rMag)));
    trueAnomaly = wrapTwoPi(rDotV < 0 ? -nu : nu);
  } else {
    const argp = Math.acos(clampUnit(dot(n, eVec) / (nMag * e)));
    argumentOfPeriapsis = wrapTwoPi(eVec[2] < 0 ? -argp : argp);
    const nu = Math.acos(clampUnit(dot(eVec, position) / (e * rMag)));
    trueAnomaly = wrapTwoPi(rDotV < 0 ? -nu : nu);
  }

  return {
    semiMajorAxis: a,
    eccentricity: e,
    inclination,
    raan,
    argumentOfPeriapsis,
    trueAnomaly,
    circular: isCircular || undefined,
    equatorial: isEquatorial || undefined,
  };
}

/**
 * Convert classical orbital elements to a state vector.
 *
 * Builds the state in the perifocal frame, where it is trivial, then applies the
 * three rotations (argument of periapsis, inclination, RAAN) that orient the
 * orbit in space.
 */
export function elementsToState(elements: OrbitalElements, mu: number): StateVector {
  const { eccentricity: e, inclination: i, raan, argumentOfPeriapsis: argp, trueAnomaly: nu } =
    elements;

  const p = Number.isFinite(elements.semiMajorAxis)
    ? elements.semiMajorAxis * (1 - e * e)
    : // Parabolic: the caller must supply the semi-latus rectum via a periapsis
      // radius encoded as a = Infinity, e = 1. Fall back to a unit p.
      1;

  const cosNu = Math.cos(nu);
  const sinNu = Math.sin(nu);
  const rMag = p / (1 + e * cosNu);

  // Perifocal frame: x toward periapsis, y 90 degrees ahead in the direction of motion.
  const rPerifocal: Vec3 = [rMag * cosNu, rMag * sinNu, 0];
  const sqrtMuOverP = Math.sqrt(mu / p);
  const vPerifocal: Vec3 = [-sqrtMuOverP * sinNu, sqrtMuOverP * (e + cosNu), 0];

  const cosArgp = Math.cos(argp);
  const sinArgp = Math.sin(argp);
  const cosI = Math.cos(i);
  const sinI = Math.sin(i);
  const cosRaan = Math.cos(raan);
  const sinRaan = Math.sin(raan);

  // Combined 3-1-3 rotation matrix from perifocal to inertial.
  const m11 = cosRaan * cosArgp - sinRaan * sinArgp * cosI;
  const m12 = -cosRaan * sinArgp - sinRaan * cosArgp * cosI;
  const m21 = sinRaan * cosArgp + cosRaan * sinArgp * cosI;
  const m22 = -sinRaan * sinArgp + cosRaan * cosArgp * cosI;
  const m31 = sinArgp * sinI;
  const m32 = cosArgp * sinI;

  return {
    position: [
      m11 * rPerifocal[0] + m12 * rPerifocal[1],
      m21 * rPerifocal[0] + m22 * rPerifocal[1],
      m31 * rPerifocal[0] + m32 * rPerifocal[1],
    ],
    velocity: [
      m11 * vPerifocal[0] + m12 * vPerifocal[1],
      m21 * vPerifocal[0] + m22 * vPerifocal[1],
      m31 * vPerifocal[0] + m32 * vPerifocal[1],
    ],
  };
}

// --- Universal-variable propagation -----------------------------------------

/**
 * Crossover between the series and closed-form Stumpff evaluations.
 *
 * The closed forms contain `1 - cos(sqrt(psi))` and `sqrt(psi) - sin(sqrt(psi))`,
 * both of which cancel catastrophically as psi approaches zero: the numerator
 * shrinks like psi while the rounding error stays at machine epsilon, so the
 * relative error grows like 2e-16 / psi. At psi = 1e-6 that is already 2e-10 —
 * enough to show up as a visible discontinuity in the propagator near the
 * parabolic case.
 *
 * Conversely the series loses accuracy as psi grows, so the crossover is a
 * balance. Measured discontinuity at the switch point, for the six-term series
 * used below: 8.6e-16 at psi = 0.1, against 2.1e-9 for a five-term series
 * switching at psi = 1. This pairing is at machine precision.
 */
const STUMPFF_SERIES_LIMIT = 0.1;

/**
 * Stumpff function c2(psi) = (1 - cos(sqrt(psi))) / psi, continued analytically
 * to negative arguments.
 */
export function stumpffC2(psi: number): number {
  if (psi > STUMPFF_SERIES_LIMIT) {
    const s = Math.sqrt(psi);
    return (1 - Math.cos(s)) / psi;
  }
  if (psi < -STUMPFF_SERIES_LIMIT) {
    const s = Math.sqrt(-psi);
    return (1 - Math.cosh(s)) / psi;
  }
  // sum_{k>=0} (-psi)^k / (2k+2)!
  const p2 = psi * psi;
  const p3 = p2 * psi;
  return (
    0.5 -
    psi / 24 +
    p2 / 720 -
    p3 / 40_320 +
    p2 * p2 / 3_628_800 -
    p2 * p3 / 479_001_600
  );
}

/**
 * Stumpff function c3(psi) = (sqrt(psi) - sin(sqrt(psi))) / psi^1.5, continued
 * analytically to negative arguments.
 */
export function stumpffC3(psi: number): number {
  if (psi > STUMPFF_SERIES_LIMIT) {
    const s = Math.sqrt(psi);
    return (s - Math.sin(s)) / (psi * s);
  }
  if (psi < -STUMPFF_SERIES_LIMIT) {
    const s = Math.sqrt(-psi);
    return (Math.sinh(s) - s) / (s * s * s);
  }
  // sum_{k>=0} (-psi)^k / (2k+3)!
  const p2 = psi * psi;
  const p3 = p2 * psi;
  return (
    1 / 6 -
    psi / 120 +
    p2 / 5040 -
    p3 / 362_880 +
    p2 * p2 / 39_916_800 -
    p2 * p3 / 6_227_020_800
  );
}

/**
 * Analytically propagate a state vector forward (or backward) by `dt` on a pure
 * two-body orbit, using universal variables.
 *
 * Universal variables rather than a per-conic-type branch because the same code
 * then handles a circular LEO, a highly eccentric transfer and a hyperbolic
 * escape without the caller having to know which it has — and because a
 * near-parabolic orbit does not blow up.
 *
 * Vallado, *Fundamentals of Astrodynamics and Applications*, algorithm 8.
 */
export function propagateKepler(
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  dt: number,
  mu: number,
): StateVector {
  if (dt === 0) {
    return { position: [position[0], position[1], position[2]], velocity: [velocity[0], velocity[1], velocity[2]] };
  }

  const sqrtMu = Math.sqrt(mu);
  const r0 = norm(position);
  const v0 = norm(velocity);
  const rDotV = dot(position, velocity);

  // alpha = 1/a: positive for ellipses, zero for parabolas, negative for hyperbolas.
  const alpha = -(v0 * v0) / mu + 2 / r0;

  let chi: number;
  if (alpha > 1e-12) {
    chi = sqrtMu * dt * alpha;
    // For a near-full-revolution step the linear guess can overshoot; bound it.
    const period = TAU / (sqrtMu * alpha ** 1.5);
    if (Math.abs(dt) > period) chi = sqrtMu * (dt % period) * alpha;
  } else if (alpha < -1e-12) {
    const a = 1 / alpha;
    const sign = Math.sign(dt);
    const numerator = -2 * mu * alpha * dt;
    const denominator = rDotV + sign * Math.sqrt(-mu * a) * (1 - r0 * alpha);
    chi = sign * Math.sqrt(-a) * Math.log(Math.max(1e-300, numerator / denominator));
  } else {
    // Parabolic. Start from the linearised time-of-flight.
    chi = (sqrtMu * dt) / r0;
  }

  let psi = 0;
  let c2 = 0.5;
  let c3 = 1 / 6;
  let r = r0;

  let converged = false;
  for (let i = 0; i < 200; i++) {
    psi = chi * chi * alpha;
    c2 = stumpffC2(psi);
    c3 = stumpffC3(psi);
    r =
      chi * chi * c2 +
      (rDotV / sqrtMu) * chi * (1 - psi * c3) +
      r0 * (1 - psi * c2);
    if (r === 0) break;
    const residual =
      sqrtMu * dt -
      chi * chi * chi * c3 -
      (rDotV / sqrtMu) * chi * chi * c2 -
      r0 * chi * (1 - psi * c3);
    const step = residual / r;
    chi += step;
    if (Math.abs(step) < 1e-10) {
      converged = true;
      break;
    }
  }
  if (!converged) {
    // Recompute the auxiliaries at the final chi so the Lagrange coefficients are
    // at least self-consistent, then hand back the best available answer. A
    // non-convergent case here means an extreme near-parabolic state; the caller
    // sees a slightly inaccurate step rather than NaNs.
    psi = chi * chi * alpha;
    c2 = stumpffC2(psi);
    c3 = stumpffC3(psi);
    r = chi * chi * c2 + (rDotV / sqrtMu) * chi * (1 - psi * c3) + r0 * (1 - psi * c2);
  }

  const f = 1 - (chi * chi * c2) / r0;
  const g = dt - (chi * chi * chi * c3) / sqrtMu;
  const gDot = 1 - (chi * chi * c2) / r;
  const fDot = (sqrtMu / (r * r0)) * chi * (psi * c3 - 1);

  return {
    position: [
      f * position[0] + g * velocity[0],
      f * position[1] + g * velocity[1],
      f * position[2] + g * velocity[2],
    ],
    velocity: [
      fDot * position[0] + gDot * velocity[0],
      fDot * position[1] + gDot * velocity[1],
      fDot * position[2] + gDot * velocity[2],
    ],
  };
}

/**
 * Sample `count` points around a closed orbit, for drawing. Samples uniformly in
 * eccentric anomaly rather than true anomaly, which distributes points evenly
 * along the *path* instead of bunching them all at periapsis.
 */
export function sampleOrbit(
  elements: OrbitalElements,
  mu: number,
  count = 180,
): Vec3[] {
  const points: Vec3[] = [];
  const e = elements.eccentricity;

  if (e >= 1) {
    // Open orbit: sweep true anomaly between the asymptotes, stopping short of
    // them so the drawn arc stays finite.
    const nuLimit = Math.acos(clampUnit(-1 / e)) * 0.98;
    for (let k = 0; k < count; k++) {
      const nu = -nuLimit + (2 * nuLimit * k) / (count - 1);
      points.push(elementsToState({ ...elements, trueAnomaly: nu }, mu).position);
    }
    return points;
  }

  for (let k = 0; k < count; k++) {
    const E = (TAU * k) / count;
    const nu = trueFromEccentric(E, e);
    points.push(elementsToState({ ...elements, trueAnomaly: nu }, mu).position);
  }
  return points;
}

/** Angular momentum vector, m^2/s. */
export const angularMomentum = (r: ReadonlyVec3, v: ReadonlyVec3): Vec3 => cross(r, v);

/** Unit normal to the orbital plane. */
export const orbitNormal = (r: ReadonlyVec3, v: ReadonlyVec3): Vec3 => normalize(cross(r, v));

/** Eccentricity vector, pointing from the focus toward periapsis. */
export function eccentricityVector(r: ReadonlyVec3, v: ReadonlyVec3, mu: number): Vec3 {
  const rMag = norm(r);
  const vSq = dot(v, v);
  const rDotV = dot(r, v);
  return [
    ((vSq - mu / rMag) * r[0] - rDotV * v[0]) / mu,
    ((vSq - mu / rMag) * r[1] - rDotV * v[1]) / mu,
    ((vSq - mu / rMag) * r[2] - rDotV * v[2]) / mu,
  ];
}

/** Time from periapsis to a given true anomaly, s. */
export function timeSincePeriapsis(
  trueAnomaly: number,
  elements: OrbitalElements,
  mu: number,
): number {
  const { eccentricity: e, semiMajorAxis: a } = elements;
  const M = meanFromTrue(trueAnomaly, e);
  if (e < 1) return M / meanMotion(a, mu);
  return M / Math.sqrt(mu / Math.abs(a) ** 3);
}

/** Position on the orbit at a given time offset from the element epoch. */
export function elementsAtTime(
  elements: OrbitalElements,
  dt: number,
  mu: number,
): OrbitalElements {
  const { eccentricity: e, semiMajorAxis: a } = elements;
  const M0 = meanFromTrue(elements.trueAnomaly, e);
  const n = e < 1 ? meanMotion(a, mu) : Math.sqrt(mu / Math.abs(a) ** 3);
  const M = M0 + n * dt;
  return { ...elements, trueAnomaly: trueFromMean(e < 1 ? wrapTwoPi(M) : M, e) };
}

/** Scale a state vector's velocity — used by the manoeuvre planner. */
export const applyDeltaV = (state: StateVector, deltaV: ReadonlyVec3): StateVector => ({
  position: [...state.position] as Vec3,
  velocity: [
    state.velocity[0] + deltaV[0],
    state.velocity[1] + deltaV[1],
    state.velocity[2] + deltaV[2],
  ],
});

/** Unit prograde direction of a state, for expressing manoeuvres. */
export const progradeDirection = (state: StateVector): Vec3 => normalize(state.velocity);

/** Radial-out direction of a state. */
export const radialDirection = (state: StateVector): Vec3 => normalize(state.position);

/** Orbit-normal direction of a state. */
export const normalDirection = (state: StateVector): Vec3 =>
  normalize(cross(state.position, state.velocity));

/** Convenience for tests and the optimizer: speed at a radius on an orbit. */
export const speedAtRadius = (radius: number, elements: OrbitalElements, mu: number): number =>
  visViva(radius, elements.semiMajorAxis, mu);

/** Reverse a state vector's velocity, giving the retrograde state. */
export const reverseState = (state: StateVector): StateVector => ({
  position: [...state.position] as Vec3,
  velocity: scale(state.velocity, -1),
});
